<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MailAccountConfig',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const recipient = ref(null);
  const sendMailTestResult = ref(null);
  const formId = ref(null);
  const sendTestEmailFormId = ref(null);
  const formName = ref('MailAccountConfig');
  const sendTestEmailFormName = ref('SendTestEmail');
  const config = ref({
    tls: {
      label: $fn.value.pgettext('MailAccount', 'Use TLS'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Enable TLS encryption for the connection.'
      ),
      default: true,
    },
    ssl: {
      label: $fn.value.pgettext('MailAccount', 'SSL'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Use SSL to secure the connection.'
      ),
      default: true,
    },
    use_credentials: {
      label: $fn.value.pgettext('MailAccount', 'Use Credentials'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Require username and password to authenticate.'
      ),
      default: true,
    },
    validate_certs: {
      label: $fn.value.pgettext('MailAccount', 'Validate Certificates'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Check that server certificates are valid.'
      ),
      default: true,
    },
    mail_from_name: {
      label: $fn.value.pgettext('MailAccount', 'Mail From Name'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Name displayed as the sender of the email.'
      ),
    },
    server: {
      label: $fn.value.pgettext('MailAccount', 'Server'),
      help: $fn.value.pgettext('MailAccount', 'Address of the mail server.'),
      default: 'smtp.server.com',
    },
    port: {
      label: $fn.value.pgettext('MailAccount', 'Port'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Port number for the mail server connection.'
      ),
      default: 587,
    },
    debug: {
      label: $fn.value.pgettext('MailAccount', 'Debug'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Show detailed connection and sending logs.'
      ),
      default: false,
    },
    username: {
      label: $fn.value.pgettext('MailAccount', 'Username'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Your login name for authentication.'
      ),
    },
    password: {
      label: $fn.value.pgettext('MailAccount', 'Password'),
      help: $fn.value.pgettext('MailAccount', 'Your account password.'),
    },
    confirm_password: {
      label: $fn.value.pgettext('MailAccount', 'Confirm Password'),
      help: $fn.value.pgettext('MailAccount', 'Re-enter password to verify.'),
    },
    timeout: {
      label: $fn.value.pgettext('MailAccount', 'Timeout'),
      help: $fn.value.pgettext('MailAccount', 'Time to wait before giving up.'),
      default: 60,
    },
    description: {
      label: $fn.value.pgettext('MailAccount', 'Description'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Short note or label for this entry.'
      ),
    },
    address: {
      label: $fn.value.pgettext('MailAccount', 'Address'),
      help: $fn.value.pgettext(
        'MailAccount',
        'Email or server address to use.'
      ),
      default: 'username@provider.com',
    },
  });
  const resource = ref(null);
  const editor = ref(null);
  const change_password = ref({
    password: null,
    confirm_password: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    recipient: recipient.value,
    sendMailTestResult: sendMailTestResult.value,
    formId: formId.value,
    sendTestEmailFormId: sendTestEmailFormId.value,
    formName: formName.value,
    sendTestEmailFormName: sendTestEmailFormName.value,
    config: config.value,
    resource: resource.value,
    editor: editor.value,
    change_password: change_password.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_recipient = async () => {
    return $app.value.auth.user.email;
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_sendTestEmailFormId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_resource = async () => {
    return props.oid
      ? $fn.value.fetch('get_object_by_type', {
          parameters: { id: props.oid, object_type: 'mail-account' },
          asResource: true,
        })
      : new $fn.value.Resource(
          _.reduce($data.value.config, (m, v, k) =>
            _.set(m, k, _.get(v, 'default', null))
          )
        );
  };
  const $$load_editor = async () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'recipient' },
      { name: 'sendMailTestResult' },
      { name: 'formId' },
      { name: 'sendTestEmailFormId' },
      { name: 'formName' },
      { name: 'sendTestEmailFormName' },
      { name: 'config' },
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'change_password' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      recipient: $$load_recipient,
      formId: $$load_formId,
      sendTestEmailFormId: $$load_sendTestEmailFormId,
      resource: $$load_resource,
      editor: $$load_editor,
    },
    ux: {},
    dataRefs: {
      recipient,
      sendMailTestResult,
      formId,
      sendTestEmailFormId,
      formName,
      sendTestEmailFormName,
      config,
      resource,
      editor,
      change_password,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext(
            'MailAccountConfig|Title',
            'Mail account configuration'
          ),
        ],
        icon: $config.value.settings.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_rq8vwigi8_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_xaexb2cft_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_or8d5n44q_getActionArgs = async ($event) => {
    return {
      objectType: 'object_by_type',
      op: 'upsert',
      data: {
        ...editor.value.getData(),
        object_type: 'mail-account',
        ...(change_password.value.password &&
        change_password.value.confirm_password
          ? change_password.value
          : {}),
      },
    };
  };
  const act_4186377094414664_closeModal_2rij3joru_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_4186377094414664_crud_e3vwtq0pn_getActionArgs = async ($event) => {
    return {
      objectType: 'object_by_type',
      op: 'upsert',
      data: {
        ...editor.value.getData(),
        object_type: 'mail-account',
        ...(change_password.value.password &&
        change_password.value.confirm_password
          ? change_password.value
          : {}),
      },
    };
  };
  const act_2928017519152566_setData_yv8x5y15j_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'sendMailTestResult',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2928017519152566_script_p50fn0nyt_value_callable = async (
    $event
  ) => {
    return (sendMailTestResult.value = 'success');
  };
  const act_2928017519152566_script_p50fn0nyt_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2928017519152566_script_p50fn0nyt_value_callable($event);
      },
    };
  };
  const act_2928017519152566_request_j8zeenbgv_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'post_sendmail_test',
      data: { mail_account: resource.value.id, recipient: recipient.value },
    };
  };
  const act_2140198529698299_closeModal_2mifx3mjl_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_request_i2sspscuo_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_object_by_type',
      data: resource.value.getData({ fields: ['id', 'etag'] }),
    };
  };
  const act_2167826738365259_closeModal_hvg9cg69o_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
    };
  };

  const act_7315092382398562_closeModal_rq8vwigi8 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_rq8vwigi8_getActionArgs,
    });
  const inst_evh_7315092382398562_close_v199cnbpa =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_rq8vwigi8,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_v199cnbpa],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ej5vxckas =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_nd9j90ms4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ej5vxckas,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_nd9j90ms4],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_cjy73i4af =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_eg5kutv3m =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_cjy73i4af,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_eg5kutv3m],
    event: 'close',
  });
  const act_1040167445267876_closeModal_xaexb2cft =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_xaexb2cft_getActionArgs,
    });
  const inst_evh_1040167445267876_success_lzlausclz =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_xaexb2cft,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_or8d5n44q = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_or8d5n44q_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_lzlausclz],
  });
  const inst_evh_1040167445267876_submit_ziro5xy30 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_or8d5n44q,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_ziro5xy30],
    event: 'submit',
  });
  const act_4186377094414664_closeModal_2rij3joru =
    new actionTypes.CloseModalAction({
      actionArgs: act_4186377094414664_closeModal_2rij3joru_getActionArgs,
    });
  const inst_evh_4186377094414664_success_ctqmuzb4g =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_closeModal_2rij3joru,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_4186377094414664_crud_e3vwtq0pn = new actionTypes.CRUDAction({
    actionArgs: act_4186377094414664_crud_e3vwtq0pn_getActionArgs,
    displayName: 'save',
    events: [inst_evh_4186377094414664_success_ctqmuzb4g],
  });
  const inst_evh_4186377094414664_submit_n1szbbj5d =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_crud_e3vwtq0pn,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_4186377094414664_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4186377094414664_submit_n1szbbj5d],
    event: 'submit',
  });
  const act_2928017519152566_setData_yv8x5y15j = new actionTypes.SetDataAction({
    actionArgs: act_2928017519152566_setData_yv8x5y15j_getActionArgs,
  });
  const inst_evh_2928017519152566_clickPreventStop_b5z4l2yhj =
    new actionTypes.EventHandlerImpl({
      action: act_2928017519152566_setData_yv8x5y15j,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const act_2928017519152566_script_p50fn0nyt = new actionTypes.ScriptAction({
    actionArgs: act_2928017519152566_script_p50fn0nyt_getActionArgs,
  });
  const inst_evh_2928017519152566_success_y8wdewer4 =
    new actionTypes.EventHandlerImpl({
      action: act_2928017519152566_script_p50fn0nyt,
      event: 'success',
      displayName: 'script',
    });
  const act_2928017519152566_request_j8zeenbgv = new actionTypes.RequestAction({
    actionArgs: act_2928017519152566_request_j8zeenbgv_getActionArgs,
    displayName: 'sendTestEmail',
    events: [inst_evh_2928017519152566_success_y8wdewer4],
  });
  const inst_evh_2928017519152566_clickPreventStop_8d5nb958o =
    new actionTypes.EventHandlerImpl({
      action: act_2928017519152566_request_j8zeenbgv,
      event: 'click.prevent.stop',
      displayName: 'sendTestEmail',
    });
  const inst_evh_2928017519152566_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_2928017519152566_clickPreventStop_b5z4l2yhj,
        inst_evh_2928017519152566_clickPreventStop_8d5nb958o,
      ],
      event: 'click.prevent.stop',
    });
  const act_2140198529698299_closeModal_2mifx3mjl =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_2mifx3mjl_getActionArgs,
    });
  const inst_evh_2140198529698299_success_9rbd3zacv =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_2mifx3mjl,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2140198529698299_request_i2sspscuo = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_i2sspscuo_getActionArgs,
    displayName: 'delete',
    events: [inst_evh_2140198529698299_success_9rbd3zacv],
  });
  const inst_evh_2140198529698299_clickPreventStop_68swaacez =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_i2sspscuo,
      event: 'click.prevent.stop',
      displayName: 'delete',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_68swaacez],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_closeModal_hvg9cg69o =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_hvg9cg69o_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_55gkkzb3z =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_hvg9cg69o,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_55gkkzb3z],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_v199cnbpa,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_nd9j90ms4,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_eg5kutv3m,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_lzlausclz,
    inst_evh_1040167445267876_submit_ziro5xy30,
    save: inst_evh_1040167445267876_submit_ziro5xy30,
    inst_evh_1040167445267876_submit,
    inst_evh_4186377094414664_success_ctqmuzb4g,
    inst_evh_4186377094414664_submit_n1szbbj5d,
    inst_evh_4186377094414664_submit,
    inst_evh_2928017519152566_clickPreventStop_b5z4l2yhj,
    inst_evh_2928017519152566_success_y8wdewer4,
    inst_evh_2928017519152566_clickPreventStop_8d5nb958o,
    sendTestEmail: inst_evh_2928017519152566_clickPreventStop_8d5nb958o,
    inst_evh_2928017519152566_clickPreventStop,
    inst_evh_2140198529698299_success_9rbd3zacv,
    inst_evh_2140198529698299_clickPreventStop_68swaacez,
    delete: inst_evh_2140198529698299_clickPreventStop_68swaacez,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2167826738365259_clickPreventStop_55gkkzb3z,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_v199cnbpa = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_v199cnbpa.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_nd9j90ms4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_nd9j90ms4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_eg5kutv3m = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_eg5kutv3m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_lzlausclz = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_lzlausclz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_ziro5xy30 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_ziro5xy30.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_success_ctqmuzb4g = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_success_ctqmuzb4g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit_n1szbbj5d = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit_n1szbbj5d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2928017519152566_clickPreventStop_b5z4l2yhj = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2928017519152566_clickPreventStop_b5z4l2yhj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2928017519152566_success_y8wdewer4 = async (event, scope) => {
    return await actions.inst_evh_2928017519152566_success_y8wdewer4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2928017519152566_clickPreventStop_8d5nb958o = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2928017519152566_clickPreventStop_8d5nb958o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2928017519152566_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2928017519152566_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_9rbd3zacv = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_9rbd3zacv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_68swaacez = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_68swaacez.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_55gkkzb3z = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_55gkkzb3z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MailAccountConfig"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :readonly="resource.locked"
        :labelAlign="$config.forms.defaultLabelAlign"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpRow>
          <ItpCol>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="address"
              :label="config.address.label"
              :helpTooltip="config.address.help"
              required
            >
              <ItpFormInput
                name="address"
                v-model="editor.address"
                :value="editor.address"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.address =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="description"
              :label="config.description.label"
              :helpTooltip="config.description.help"
            >
              <ItpFormInput
                name="description"
                v-model="editor.description"
                :value="editor.description"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.description =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormInput>
            </ItpFormGroup>
            <hr />
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="use_credentials"
              :label="config.use_credentials.label"
              :helpTooltip="config.use_credentials.help"
            >
              <ItpFormCheckbox
                name="use_credentials"
                v-model="editor.use_credentials"
                :value="editor.use_credentials"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.use_credentials =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="username"
              :label="config.username.label"
              :helpTooltip="config.username.help"
              required
            >
              <ItpFormInput
                name="username"
                v-model="editor.username"
                :value="editor.username"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.username =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="password"
              :label="config.password.label"
              :helpTooltip="config.password.help"
            >
              <ItpFormInput
                type="password"
                name="password"
                v-model="change_password.password"
                :value="change_password.password"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (change_password.password =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="confirm_password"
              :label="config.confirm_password.label"
              :helpTooltip="config.confirm_password.help"
            >
              <ItpFormInput
                type="password"
                name="confirm_password"
                v-model="change_password.confirm_password"
                :value="change_password.confirm_password"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (change_password.confirm_password =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormInput>
            </ItpFormGroup>
            <hr />
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="tls"
              :label="config.tls.label"
              :helpTooltip="config.tls.help"
            >
              <ItpFormCheckbox
                name="tls"
                v-model="editor.tls"
                :value="editor.tls"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.tls =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="ssl"
              :label="config.ssl.label"
              :helpTooltip="config.ssl.help"
            >
              <ItpFormCheckbox
                name="ssl"
                v-model="editor.ssl"
                :value="editor.ssl"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.ssl =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="server"
              :label="config.server.label"
              :helpTooltip="config.server.help"
              required
            >
              <ItpFormInput
                name="server"
                v-model="editor.server"
                :value="editor.server"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.server =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="2"
              name="port"
              :label="config.port.label"
              :helpTooltip="config.port.help"
              required
            >
              <ItpFormInput
                type="number"
                name="port"
                v-model="editor.port"
                :value="editor.port"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.port =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="mail_from_name"
              :label="config.mail_from_name.label"
              :helpTooltip="config.mail_from_name.help"
              required
            >
              <ItpFormInput
                name="mail_from_name"
                v-model="editor.mail_from_name"
                :value="editor.mail_from_name"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.mail_from_name =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="validate_certs"
              :label="config.validate_certs.label"
              :helpTooltip="config.validate_certs.help"
            >
              <ItpFormCheckbox
                name="validate_certs"
                v-model="editor.validate_certs"
                :value="editor.validate_certs"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.validate_certs =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="2"
              name="timeout"
              :label="config.timeout.label"
              :helpTooltip="config.timeout.help"
              required
              contentClass="input-group"
            >
              <ItpFormInput
                type="number"
                name="timeout"
                v-model="editor.timeout"
                :value="editor.timeout"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (editor.timeout =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
              <span class="input-group-text"> sec </span>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
      <ItpForm
        :name="sendTestEmailFormName"
        :labelAlign="$config.forms.defaultLabelAlign"
        v-if="!resource.isNew"
        @submit="evh_4186377094414664_submit($event, {})"
      >
        <hr />
        <ItpFormGroup
          :label-cols-md="3"
          :content-cols-md="9"
          name="recipient"
          :label="pgettext('MailAccountConfig', 'Send test email')"
          required
        >
          <div class="input-group">
            <ItpFormInput
              name="recipient"
              v-model="recipient"
              :value="recipient"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (recipient =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              required
            >
            </ItpFormInput>
            <ItpButton
              type="submit"
              :text="pgettext('MailAccountConfig|Button', 'Send')"
              :spinning="actions.sendTestEmail.isRunning"
              @click.prevent.stop="
                evh_2928017519152566_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </div>
          <ItpAlert
            type="success"
            :show="sendMailTestResult === 'success'"
            dismissible
            class="mt-3"
          >
            <span>
              {{
                pgettext(
                  'MailAccountConfig',
                  'Email has been successfully sent.'
                )
              }}
            </span>
          </ItpAlert>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Delete')"
          variant="danger"
          icon="fa-times"
          :spinning="actions.delete.isRunning"
          class="me-auto"
          v-if="!resource.isNew"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :form="forms[formName] && forms[formName].id"
          :text="pgettext('Button', 'Save')"
          type="submit"
          variant="primary"
          default
          :disabled="!forms[formName] || !forms[formName].submittable"
          :spinning="actions.submit && actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Close')"
          variant="default"
          :disabled="actions.submit && actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
